"use client";

import { useCallback, useEffect, useRef, startTransition, useState } from "react";
import { onIdTokenChanged, GoogleAuthProvider, OAuthProvider, getMultiFactorResolver, signInWithRedirect, getRedirectResult, signOut as firebaseSignOut, type MultiFactorError, type MultiFactorResolver } from "firebase/auth";
import { auth } from "@/lib/firebase/client";
import { useRouter } from "next/navigation";
import { AuthContext, User } from "@/lib/auth/AuthContext";
import { syncUser } from "@/app/actions/auth/sync-user";
import { isApexHost } from "@/lib/auth/apex-hosts";
import { isLoginPath, syncSessionCookie } from "@/lib/auth/login-handoff";
import { MFA_ERROR_CODES, attestMfa, isFirebaseError } from "@/lib/auth/mfa-client";
import { clearSessionHintCookie, setSessionHintCookie } from "@/lib/auth/session-hint";
export { useAuth } from "@/lib/auth/AuthContext";
const ACTIVE_CHAT_STORAGE_KEY = "audacity:active-chat-id";

// Shown on the login form when the session cookie could not be established
// (AIR-819). Deliberately plain: the user's next step is simply to retry.
export const SIGN_IN_FAILED_MESSAGE = "We couldn't finish signing you in. Please try again.";

// Belt-and-braces for the non-login refresh path: after a verified cookie
// sync, router.refresh() should come back with the server-rendered user prop.
// If it hasn't after this long, fall back to one full reload, which re-runs
// the server render with the cookie present. (The login page itself no longer
// depends on this — see completeSignIn.)
const REFRESH_WATCHDOG_MS = 8_000;
// Reloads draw from a small per-tab budget so a persistently failing server
// (or a browser that refuses cookies) can't put the tab in a reload loop.
const RELOAD_BUDGET_KEY = "audacity:auth-refresh-reloads";
const RELOAD_BUDGET_WINDOW_MS = 5 * 60_000;
const RELOAD_BUDGET_MAX = 2;
function consumeReloadBudget(): boolean {
  try {
    const now = Date.now();
    const stamps = (JSON.parse(sessionStorage.getItem(RELOAD_BUDGET_KEY) ?? "[]") as number[]).filter(t => now - t < RELOAD_BUDGET_WINDOW_MS);
    if (stamps.length >= RELOAD_BUDGET_MAX) return false;
    stamps.push(now);
    sessionStorage.setItem(RELOAD_BUDGET_KEY, JSON.stringify(stamps));
    return true;
  } catch {
    return false;
  }
}
interface AuthProviderProps {
  user: User | null;
  children: React.ReactNode;
}

/**
 * Syncs Firebase auth state to server by calling /api/login with the ID token.
 * This sets the httpOnly session cookies that the server needs.
 * Also updates the user's lastLoginAt timestamp in the database.
 *
 * Throws when the session cookie could not be established (AIR-819): callers
 * must not proceed with a handoff that assumes a server session exists.
 */
async function syncAuthToServer(idToken: string): Promise<void> {
  // First, sync auth cookies to server. Verified and retried — a transient
  // /api/login failure (cold-instance token verification) used to be silently
  // ignored here, leaving the client waiting forever for a session that was
  // never created.
  await syncSessionCookie(idToken);

  // Session confirmed server-side — publish the non-httpOnly landing hint the
  // static marketing page at "/" keys off (AIR-877).
  setSessionHintCookie();

  // Then, update lastLoginAt in database (and create the DB user on first
  // login). Called after cookies are set so syncUser can read the token.
  let sessionToken = idToken;
  try {
    const result = await syncUser();
    // AIR-1261: a SAML reconciliation just stamped clientId/role claims onto
    // this Firebase account, but the cookie minted above still carries the
    // pre-stamp token, which access control fails closed (stale_client_claim).
    // Re-mint the session from a force-refreshed token so the first SSO
    // sign-in proceeds; if the refresh fails, the session simply stays
    // denied server-side and the user is asked to log in again.
    if (result.reconciled) {
      const freshToken = await auth.currentUser?.getIdToken(true);
      if (freshToken) {
        await syncSessionCookie(freshToken);
        sessionToken = freshToken;
      }
    }
  } catch (error) {
    // Log error but don't block login flow
    console.error("[Auth] Failed to sync user login timestamp:", error);
  }

  // Two-factor attestation (AIR-1500): the session cookie cannot say whether
  // this sign-in passed a second factor, so prove it with the ID token while
  // it is fresh. 403 is the everyday answer for accounts without a second
  // factor and is not an error; anything else is a failed handoff and is
  // surfaced exactly like a failed cookie sync (the caller catches).
  await attestMfa(sessionToken);
}
export function AuthProvider({
  user,
  children
}: AuthProviderProps) {
  const router = useRouter();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [mfaChallenge, setMfaChallenge] = useState<MultiFactorResolver | null>(null);
  const clearMfaChallenge = useCallback(() => setMfaChallenge(null), []);

  // Track the last synced UID - initialize with server-provided user
  const lastSyncedUid = useRef<string | null>(user?.uid ?? null);
  // Track if we've handled the initial auth sync
  const hasHandledInitialSync = useRef(false);

  // Refresh watchdog (AIR-819 mitigation, kept as belt-and-braces for the
  // non-login refresh path): armed on every sync-triggered refresh, disarmed
  // when the server-rendered user arrives.
  const refreshWatchdog = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshWithWatchdog = useCallback(() => {
    if (refreshWatchdog.current) clearTimeout(refreshWatchdog.current);
    refreshWatchdog.current = setTimeout(() => {
      if (consumeReloadBudget()) {
        window.location.reload();
      } else {
        // Budget exhausted — stop pretending progress is being made and let
        // the login form take another attempt.
        setIsAuthenticating(false);
      }
    }, REFRESH_WATCHDOG_MS);
    startTransition(() => {
      router.refresh();
    });
  }, [router]);

  // Hand off to the server after a VERIFIED cookie sync (AIR-819 root-cause
  // fix). On the login page, don't refresh-and-wait for the user prop —
  // navigate. The full request re-runs the middleware, which redirects
  // authenticated visitors off /login to their role destination (or honors
  // ?redirect=) server-side. Should the middleware pass the request through
  // instead (e.g. a transient verification error on a public path), the page
  // renders with the user prop and the login page's own user-effect completes
  // the redirect — every branch makes forward progress. The reload draws from
  // the same per-tab budget as the watchdog so a browser that refuses cookies
  // can't loop; each successful sign-in clears the budget.
  const completeSignIn = useCallback(() => {
    if (isLoginPath(window.location.pathname)) {
      if (consumeReloadBudget()) {
        window.location.reload();
      } else {
        setAuthError(SIGN_IN_FAILED_MESSAGE);
        setIsAuthenticating(false);
      }
      return;
    }
    // Elsewhere (e.g. a public page whose server session expired) the page
    // just re-renders authenticated in place.
    refreshWithWatchdog();
  }, [refreshWithWatchdog]);

  // Reset isAuthenticating when user prop updates (after router.refresh completes)
  useEffect(() => {
    if (user) {
      setIsAuthenticating(false);
      setAuthError(null);
      // Live server session on this page load — (re)publish the landing hint
      // (AIR-877). Seeds sessions that predate the hint cookie and slides its
      // expiry alongside __session's refresh.
      setSessionHintCookie();
      if (refreshWatchdog.current) {
        clearTimeout(refreshWatchdog.current);
        refreshWatchdog.current = null;
      }
      try {
        sessionStorage.removeItem(RELOAD_BUDGET_KEY);
      } catch {
        // sessionStorage unavailable — nothing to clean
      }
    }
  }, [user]);

  // Never leave a timer behind on unmount
  useEffect(() => () => {
    if (refreshWatchdog.current) clearTimeout(refreshWatchdog.current);
  }, []);

  // Handle redirect result on mount - this is critical for signInWithRedirect
  useEffect(() => {
    async function handleRedirectResult() {
      try {
        setIsAuthenticating(true);
        const result = await getRedirectResult(auth);
        if (result?.user) {
          setAuthError(null);
          const idToken = await result.user.getIdToken();
          await syncAuthToServer(idToken);
          lastSyncedUid.current = result.user.uid;
          hasHandledInitialSync.current = true;
          completeSignIn();
        } else {
          setIsAuthenticating(false);
        }
      } catch (error) {
        // A redirect sign-in stopped for a second factor (AIR-1500) is not a
        // failure: hand the resolver to the login page, which asks for the
        // authenticator code and completes the sign-in.
        if (isFirebaseError(error, MFA_ERROR_CODES.multiFactorRequired)) {
          setMfaChallenge(getMultiFactorResolver(auth, error as MultiFactorError));
          setIsAuthenticating(false);
          return;
        }
        console.error("[Auth] Error handling redirect result:", error);
        setAuthError(SIGN_IN_FAILED_MESSAGE);
        setIsAuthenticating(false);
      }
    }
    handleRedirectResult();
  }, [completeSignIn]);

  // Handle ongoing auth state changes (login/logout within the same session)
  useEffect(() => {
    // Public pages served on the marketing apex (legal, status, report) can
    // never hold a session: /api/login there 308s to the portal host and the
    // browser drops the Authorization header on the cross-origin hop, so the
    // sync can only ever fail (400) and flash "couldn't finish signing you
    // in" at someone who was just reading a legal page. A Firebase user left
    // in that origin's storage by an older sign-in is simply ignored.
    if (isApexHost(window.location.hostname)) {
      hasHandledInitialSync.current = true;
      return;
    }
    const unsubscribe = onIdTokenChanged(auth, async firebaseUser => {
      const currentUid = firebaseUser?.uid ?? null;

      // On first callback, check if we need to sync
      // This handles the case where user is already logged in (persisted session)
      // but server doesn't have cookies (e.g., cookies expired, new tab)
      if (!hasHandledInitialSync.current) {
        hasHandledInitialSync.current = true;

        // If Firebase has a user but server doesn't, sync to server
        if (firebaseUser && !user) {
          setIsAuthenticating(true);
          setAuthError(null);
          try {
            const idToken_0 = await firebaseUser.getIdToken();
            await syncAuthToServer(idToken_0);
            // Marked synced only on success — a later sign-in event for the
            // same uid must retry, not be skipped as a no-op.
            lastSyncedUid.current = currentUid;
            completeSignIn();
          } catch (error_0) {
            console.error("[Auth] Failed to establish server session:", error_0);
            setAuthError(SIGN_IN_FAILED_MESSAGE);
            setIsAuthenticating(false);
          }
          return;
        }

        // Definitively signed out (no client auth state, no server session):
        // drop any stale landing hint so "/" serves marketing again (AIR-877).
        // Reached by e.g. a session that expired or was cleared server-side
        // without the client sign-out flow running.
        if (!firebaseUser && !user) {
          clearSessionHintCookie();
        }
        lastSyncedUid.current = currentUid;
        return;
      }

      // Skip if user hasn't changed
      if (currentUid === lastSyncedUid.current) {
        return;
      }
      const previousUid = lastSyncedUid.current;
      if (firebaseUser) {
        setIsAuthenticating(true);
        setAuthError(null);
        try {
          const idToken_1 = await firebaseUser.getIdToken();
          await syncAuthToServer(idToken_1);
          lastSyncedUid.current = currentUid;
          completeSignIn();
        } catch (error_1) {
          console.error("[Auth] Failed to establish server session:", error_1);
          setAuthError(SIGN_IN_FAILED_MESSAGE);
          setIsAuthenticating(false);
        }
      } else if (previousUid !== null) {
        lastSyncedUid.current = currentUid;
        localStorage.removeItem(ACTIVE_CHAT_STORAGE_KEY);
        clearSessionHintCookie();
        // Guarded: a rejected logout fetch (network drop, server down) must
        // not strand the user here — the redirect below always runs (#1005
        // review 1a).
        await fetch("/api/logout", {
          credentials: "include"
        }).catch(() => {});
        // Session ended out from under the user (expiry, signed out in another
        // tab). Remember where they were so sign-in returns them there.
        const returnTo = window.location.pathname + window.location.search;
        window.location.href = returnTo && returnTo !== "/" && !returnTo.startsWith("/login") ? `/login?redirect=${encodeURIComponent(returnTo)}` : "/login";
      }
    });
    return () => unsubscribe();
  }, [completeSignIn, user]);
  return <AuthContext.Provider value={{
    user,
    isAuthenticating,
    authError,
    mfaChallenge,
    clearMfaChallenge
  }}>
      {children}
    </AuthContext.Provider>;
}

// Helper functions for authentication actions
export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({
    prompt: "select_account"
  });

  // Use popup for localhost (redirect has issues with auth domain mismatch)
  // Use redirect for production (popup has COOP header issues)
  if (typeof window !== "undefined" && window.location.hostname === "localhost") {
    const {
      signInWithPopup
    } = await import("firebase/auth");
    await signInWithPopup(auth, provider);
  } else {
    await signInWithRedirect(auth, provider);
  }
}
export async function signInWithMicrosoft() {
  const provider = new OAuthProvider("microsoft.com");
  provider.setCustomParameters({
    prompt: "select_account",
    tenant: "common"
  });
  provider.addScope("email");
  provider.addScope("profile");
  if (typeof window !== "undefined" && window.location.hostname === "localhost") {
    const {
      signInWithPopup
    } = await import("firebase/auth");
    await signInWithPopup(auth, provider);
  } else {
    await signInWithRedirect(auth, provider);
  }
}

/**
 * Ends the session everywhere and lands on /login. `redirectTo` (a same-origin
 * path) is carried on the login URL so the next sign-in returns there — the
 * two-factor page uses it when an enrolled account must sign in again.
 */
export async function signOut({
  redirectTo
}: {
  redirectTo?: string;
} = {}) {
  localStorage.removeItem(ACTIVE_CHAT_STORAGE_KEY);
  try {
    // Attempt Firebase signOut with a timeout
    // If the Firebase auth connection is stale (e.g., after extended idle periods),
    // the signOut call may hang or complete without triggering onIdTokenChanged
    const timeoutMs = 5000;
    const signOutPromise = firebaseSignOut(auth);
    const timeoutPromise = new Promise<never>((_, reject) => setTimeout(() => reject(new Error("SignOut timeout")), timeoutMs));
    await Promise.race([signOutPromise, timeoutPromise]);
  } catch (error) {
    console.error("[Auth] Firebase signOut failed or timed out, forcing logout:", error);
  } finally {
    // Landing hint follows the session: cleared BEFORE the logout fetch —
    // which can itself reject — so a signed-out visitor's "/" can never keep
    // bouncing into the app even when this block aborts early (AIR-877;
    // ordering per #962 review, matching the expiry path above).
    clearSessionHintCookie();
    // Always clear the server session. Relying only on onIdTokenChanged can
    // leave the httpOnly __session cookie intact if the listener is stale.
    // Guarded so a rejected fetch can't throw out of this finally block and
    // skip the redirect — the user must always land on /login (#1005 review
    // 1a).
    await fetch("/api/logout", {
      credentials: "include"
    }).catch(() => {});
    window.location.replace(redirectTo && redirectTo.startsWith("/") && !redirectTo.startsWith("//") ? `/login?redirect=${encodeURIComponent(redirectTo)}` : "/login");
  }
}